import { Module, MutationTree, ActionTree, GetterTree } from "vuex";
import { AxiosError } from "axios";
import axios from "@/shared/scripts/AutoRefreshAxios";

import { RootState } from "../../store.interface";
import { Classified } from "@/shared/interfaces/classified/Classified.interface";
import { Order, OrderApiError } from "@/shared/interfaces/order/Order.interface";
import { OrderApiStoreState } from "@/shared/interfaces/order/OrderStoreState.interface";
import { Transaction } from "@/shared/interfaces/transaction/Transaction.interface";
import router from "@/shared/router";
import { Trans } from "@/shared/scripts/helpers/translation/translation";
import {
    hasValidBillboardBillingAddress,
    isBillboardSelected,
    isBillboardTaxedPostalCode
} from "@/shared/scripts/helpers/customer/billingAddress";
import { getTariffId } from "@/shared/scripts/helpers/order/tariffId";
import { requiresPersonName } from "@/shared/scripts/helpers/customer/customer";

const DOMAIN: string = process.env.VUE_APP_PRICING_URL || "";
const API_VERSION = "2";
const ORDER_RESOURCE = "orders/";
const TRANSACTION_RESOURCE = "transactions/";

const url = DOMAIN + ORDER_RESOURCE + API_VERSION;

const state: OrderApiStoreState = {
    orderApiErrors: [],
};

const mutations: MutationTree<OrderApiStoreState> = {
    errors: function(state, errors: Array<OrderApiError>) {
        state.orderApiErrors = errors;
    },
};

// maybe move this somewhere else
interface CreateOrderOptions {
    classified?: Classified,
    type?: string,
    errorRoute?: string,
}

function resolvePaymentRedirectUrl(name: string, classifiedId: number, query = {}): string {
    const to = router.resolve({
        name: name,
        params: { id: classifiedId as unknown as string },
        query: query,
    });

    // Ogone requires "index.html" in the callback URL.
    return process.env.VUE_APP_PAYMENT_REDIRECT_URL + to.href.replace("#", "index.html#");
}

const actions: ActionTree<OrderApiStoreState, RootState> = {
    getOrderById: function({ commit, getters, rootState }, { orderId, errorRoute = "step-payment", pricingType = "PUBLICATION" }): Promise<boolean> {
        const classifiedId = rootState.FormModule.classifiedId as number;

        return new Promise((resolve, reject) => {
            axios({
                url: `${url}/${orderId}`,
                headers: getters.apiHeaders,
            })
                .then(response => {
                    if(response.data) {
                        commit("currentOrder", response.data);

                        if(response.data?.tariff.manualDiscount) {
                            commit("manualDiscountId", response.data.tariff.manualDiscount.id);
                        }

                        const acceptUrl = resolvePaymentRedirectUrl("confirmation", classifiedId, {
                            success: true,
                            action: pricingType
                        });

                        const declineUrl = resolvePaymentRedirectUrl(errorRoute, classifiedId, {
                            orderId: orderId,
                            error: true,
                        });

                        const initialTransaction: Transaction = {
                            orderId: orderId,
                            paymentMethod: null,
                            acceptUrl: acceptUrl,
                            declineUrl: declineUrl,
                            homeUrl: "https://www.immoweb.be/fr",
                        };

                        commit("transactions/initial", initialTransaction);
                    }

                    resolve(true);
                })
                .catch((e: AxiosError) => reject(e.response?.data.errors));
        });
    },

    createOrder: function({ commit, getters, rootGetters, rootState }, options: CreateOrderOptions = {}): Promise<boolean> {
        const order: Order = rootGetters["FormModule/getValueOrder"]("order");
        const classifiedId = options.classified?.id ?? rootGetters["FormModule/getClassifiedId"];
        const { visualizationOption } = order;
        const pricingType = options.type ?? "PUBLICATION";

        // Never order a billboard in a municipality that taxes it, whatever the UI state.
        const hasBillboard = !isBillboardTaxedPostalCode(rootGetters["FormModule/getValue"]("property.location.address.postalCode"))
            && isBillboardSelected(
                rootGetters["FormModule/getValueTransaction"],
                rootGetters["FormModule/getValueOrder"]
            );

        const tariffId = getTariffId(
            rootGetters["FormModule/getValueTransaction"],
            rootGetters["FormModule/getValueOrder"]
        );

        if (hasBillboard && !hasValidBillboardBillingAddress(rootState.CustomersModule.currentCustomer)) {
            const billboardAddressError: OrderApiError = {
                dataPath: "customer.location.address.provided",
                keyword: "required",
                message: "Complete billing address is required for billboard orders",
                params: {
                    hasBillboard: true,
                    requiredFields: ["street", "streetNumber", "postalCode", "locality"],
                    minStreetLength: 6,
                },
                schemaPath: "#/properties/customer/location/address/provided",
            };

            commit("errors", [billboardAddressError]);
            return Promise.reject([billboardAddressError]);
        }

        // IO-154: creating the order is what emits the B2C log to Salesforce, and Salesforce
        // rejects it with REQUIRED_FIELD_MISSING: [Name] when the customer name is blank. Every
        // funnel already guards this in the UI (shared/mixins/missingCustomerName.ts); this is the
        // net, so a new entry point cannot quietly slip an unusable name through again.
        const currentCustomer = rootState.CustomersModule.currentCustomer;

        if (requiresPersonName(currentCustomer)) {
            const missingNameError: OrderApiError = {
                dataPath: "customer.name",
                keyword: "required",
                message: "A first and last name is required on the customer before an order can be created",
                params: {
                    customerName: currentCustomer ? currentCustomer.name : null,
                },
                schemaPath: "#/properties/customer/name",
            };

            commit("errors", [missingNameError]);
            return Promise.reject([missingNameError]);
        }

        const payload: {
            visualizationOption?: string,
            tariffId?: string,
            pricingType: string,
            classifiedId: number,
            hasBillboard: boolean,
        } = {
            visualizationOption: visualizationOption,
            pricingType: pricingType,
            classifiedId: classifiedId,
            hasBillboard: hasBillboard,
            ...(pricingType !== "UPGRADE" && tariffId ? { tariffId: tariffId } : {}),
        };

        return new Promise((resolve, reject) => {
            return axios({
                method: "post",
                url: url,
                headers: getters.apiHeaders,
                data: payload
            }).then(res => {
                if (res.data) {
                    commit("currentOrder", res.data);


                    const initialTransaction: Transaction = {
                        orderId: res.data.id,
                        paymentMethod: null,
                        returnUrl: resolvePaymentRedirectUrl("payment-status", classifiedId),
                    };

                    commit("transactions/initial", initialTransaction);
                }
                resolve(true);
            })
                .catch((e: AxiosError) => reject(e.response?.data.errors));
        });
    },

    patchOrder: function({ commit, dispatch, getters, rootState }): Promise<boolean> {
        dispatch("resetErrors");

        const currentOrder = rootState.OrdersModule.currentOrder as Order;

        return new Promise((resolve, reject) => {
            axios({
                method: "patch",
                url: `${url}/${currentOrder.id}`,
                headers: getters.apiHeaders,
                data: {
                    manualDiscountId: rootState.OrdersModule.manualDiscountId,
                }
            })
                .then(response => {
                    if(response.data) {
                        commit("currentOrder", response.data);
                    }
                    resolve(true);
                })
                .catch((e: AxiosError) => {

                    /**
                     * Malformed request: uncomment when you need to return
                     * errors in order to implement errors management in
                     * PromotionalCode.vue component.
                     */
                    // // Malformed request
                    // const errors = e.response?.data;
                    //
                    // if (errors) {
                    //     commit("errors", errors); // not sure this one is the right state place
                    //     resolve(false);
                    // }

                    // Network errors
                    if (e.request) {
                        reject(e.request);
                    }

                    // Other error
                    reject(e.message);
                });
        });
    },

    createTransaction: function({ commit, getters, rootState }): Promise<any> {
        return new Promise((resolve, reject) => {
            axios({
                method: "post",
                url: DOMAIN + TRANSACTION_RESOURCE + API_VERSION,
                headers: {
                    ...getters.apiHeaders,
                    "Accept-Language": Trans.currentLocale,
                },
                data: rootState.OrdersModule.transactions?.initial,
            }).then(res => {
                if(res.data) {
                    commit("transactions/current", res.data);
                }
                resolve(res.data);
            })
                .catch((e: AxiosError) =>
                    reject(e.response?.data.errors)
                );
        });
    },

    getTransactionStatus: async function({ getters }, hostedCheckoutId) {
        try {
            const transaction = await axios({
                url: `${DOMAIN}${TRANSACTION_RESOURCE}${API_VERSION}/status`,
                method: "get",
                headers: getters.apiHeaders,
                params: { hostedCheckoutId: hostedCheckoutId }
            });

            if (!transaction.data || !transaction.data.status) {
                return "ERROR";
            }

            return transaction.data.status;

        } catch (err) {
            return "ERROR";
        }
    },


    resetErrors: function({ commit }) {
        commit("errors", []);
    },
};

const getters: GetterTree<OrderApiStoreState, RootState> =  {
    apiHeaders: ((state, getters, rootState): Record<string, string> => ({
        "x-api-key": process.env.VUE_APP_PRICING_API_KEY as string,
        "Accept": "application/json",
        "Authorization": "Bearer " + rootState.UserModule.user?.jwtToken,
    })),

    orderApiErrors: (({ orderApiErrors }: OrderApiStoreState): OrderApiError[] | null => {
        return orderApiErrors.length > 0 ? orderApiErrors : null;
    }),
};

const api: Module<OrderApiStoreState, RootState> = {
    state: state,
    mutations: mutations,
    actions: actions,
    getters: getters,
};

export default api;
